import { useMemo } from 'react'

import { useGetControllerLogs } from '@/modules/CombinedLogs'
import { calculateMileageLoss } from '@/modules/Mileage/helpers/calculateMileageLoss'
import { DisruptionLogCode, MileageLossCode } from '@/types/logs'
import { MileageLoss } from '@/modules/Mileage/types'

const getRoutePercent = (totalLoss: number, routeLoss: number): number => {
    if (totalLoss === 0) {
        return 0
    }
    return (routeLoss / totalLoss) * 100
}

export const useRouteDetails = (route: string) => {
    const { data: logs = [] } = useGetControllerLogs()

    const totalMileageLoss = useMemo(() => calculateMileageLoss(logs), [logs])

    const routeMileageLoss = useMemo(() => {
        const routeLogs = logs.filter((log) => log.routes.includes(route))
        return calculateMileageLoss(routeLogs)
    }, [route, logs])

    const routeMileageLossInfo = useMemo(() => {
        const totalML = Object.values(totalMileageLoss).reduce((sum, value) => sum + value, 0)
        const routeML = Object.values(routeMileageLoss).reduce((sum, value) => sum + value, 0)

        return {
            total: getRoutePercent(totalML, routeML),
            mileageLoss: Object.entries(routeMileageLoss).reduce(
                (acc, [key, routeValue]) => ({
                    ...acc,
                    [key]: getRoutePercent(totalMileageLoss[key as MileageLossCode], routeValue),
                }),
                {} as MileageLoss
            ),
        }
    }, [totalMileageLoss, routeMileageLoss])

    const amountOfDisruptions = useMemo(
        () =>
            logs.filter(
                (item) =>
                    item.routes.includes(route) &&
                    (Object.values(DisruptionLogCode) as string[]).includes(item.code?.toLocaleLowerCase())
            ).length,
        [logs]
    )

    const amountOfCurtailments = useMemo(
        () => logs.filter((item) => item.routes.includes(route) && !!item.bulkCurtailment).length,
        [logs]
    )

    return {
        routeMileageLossInfo,
        amountOfDisruptions,
        amountOfCurtailments,
    }
}
